/**
 * Firmen-Verwaltung (nur globaler Admin).
 *
 * - Firmen anlegen, umbenennen, Einladungs-Code anzeigen/erneuern.
 * - Mitarbeiter einer Firma zuordnen/entfernen + Rolle (Leitung/Mitarbeiter).
 * - Standorte zwischen Firmen verschieben.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth, type CompanyRole } from '../store/auth';
import {
  companiesApi,
  fetchAllLocationsRaw,
  fetchAllUsersRaw,
  moveLocation,
  type ApiCompany,
} from '../lib/companies';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { WageEscrowCard, WageRescueButton } from '../components/WageEscrow';
import { MailStatusCard } from '../components/MailStatusCard';

export default function CompanyAdminPage() {
  const token = useAuth((s) => s.token)!;
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: companies } = useQuery({ queryKey: ['companies'], queryFn: () => companiesApi.list(token) });
  const { data: allUsers } = useQuery({ queryKey: ['all-users'], queryFn: () => fetchAllUsersRaw(token) });
  const { data: allLocations } = useQuery({
    queryKey: ['all-locations'],
    queryFn: () => fetchAllLocationsRaw(token),
  });

  async function createCompany() {
    if (!newName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await companiesApi.create(token, newName.trim());
      setNewName('');
      hapticSuccess();
      await qc.invalidateQueries({ queryKey: ['companies'] });
    } catch (err) {
      hapticError();
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-10">
      <h2 className="text-xl font-bold">Firmen</h2>

      {/* Phase L: Notfall-Schlüssel für die verschlüsselten Lohndaten (Admin-Rettung) */}
      <WageEscrowCard />
      {/* v1.4.0: E-Mail-Versand (Einladungen, Passwort vergessen) */}
      <MailStatusCard />

      {/* Neue Firma */}
      <div className="card p-3 flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[12rem]">
          <span className="block text-sm text-slate-300 mb-1">Neue Firma</span>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="input"
            placeholder="Firmenname"
          />
        </label>
        <button onClick={() => void createCompany()} disabled={busy || !newName.trim()} className="btn">
          Anlegen
        </button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}

      {/* Firmen-Liste */}
      <div className="space-y-3">
        {(companies ?? []).map((c) => (
          <CompanyCard key={c.id} company={c} allUsers={allUsers ?? []} />
        ))}
        {companies && companies.length === 0 && (
          <p className="text-sm text-slate-400">Noch keine Firmen.</p>
        )}
      </div>

      {/* Standorte zuordnen */}
      <section className="card p-4">
        <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-3">Standorte zuordnen</h3>
        {(allLocations ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">Keine Standorte.</p>
        ) : (
          <ul className="space-y-2">
            {(allLocations ?? []).map((loc) => (
              <li key={loc.id} className="flex items-center gap-3">
                <span
                  className="w-3 h-6 rounded shrink-0"
                  style={{ backgroundColor: loc.color }}
                  aria-hidden
                />
                <span className="flex-1 min-w-0 truncate text-sm">{loc.name}</span>
                <select
                  value={loc.companyId ?? ''}
                  onChange={async (e) => {
                    try {
                      await moveLocation(token, loc.id, e.target.value);
                      hapticSuccess();
                      await qc.invalidateQueries({ queryKey: ['all-locations'] });
                      await qc.invalidateQueries({ queryKey: ['companies'] });
                    } catch {
                      hapticError();
                    }
                  }}
                  className="text-xs bg-slate-900 border border-border rounded px-2 py-1 !min-h-0 max-w-[10rem]"
                >
                  <option value="" disabled style={{ backgroundColor: '#0f172a', color: '#e6ebf3' }}>
                    — Firma —
                  </option>
                  {(companies ?? []).map((c) => (
                    <option key={c.id} value={c.id} style={{ backgroundColor: '#0f172a', color: '#e6ebf3' }}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CompanyCard({
  company,
  allUsers,
}: {
  company: ApiCompany;
  allUsers: { id: string; name: string; email: string }[];
}) {
  const token = useAuth((s) => s.token)!;
  const setActiveCompany = useAuth((s) => s.setActiveCompany);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(company.name);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<CompanyRole>('employee');

  const { data: members } = useQuery({
    queryKey: ['company-members', company.id],
    queryFn: () => companiesApi.members(token, company.id),
    enabled: open,
  });

  const memberIds = useMemo(() => new Set((members ?? []).map((m) => m.userId)), [members]);
  const addable = allUsers.filter((u) => !memberIds.has(u.id));

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['company-members', company.id] });
    await qc.invalidateQueries({ queryKey: ['companies'] });
  }

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={async () => {
                if (name.trim() && name.trim() !== company.name) {
                  await companiesApi.rename(token, company.id, name.trim());
                  await qc.invalidateQueries({ queryKey: ['companies'] });
                }
              }}
              className="bg-transparent font-semibold text-base border-b border-transparent focus:border-border focus:outline-none min-w-0"
            />
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {company.memberCount} Mitglieder · {company.locationCount} Standorte
          </p>
          {/* v1.3.0: die Firma so sehen, wie ihr Chef sie sieht (alle Chef-Funktionen). */}
          <button
            type="button"
            onClick={() => {
              setActiveCompany(company.id);
              void qc.resetQueries();
              navigate('/');
            }}
            className="mt-2 text-xs rounded bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 px-2.5 py-1 !min-h-0"
          >
            Als Chef ansehen →
          </button>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-slate-400">Einladungs-Code:</span>
            <code className="text-sm font-mono bg-slate-900 rounded px-2 py-0.5 tracking-widest">
              {company.joinCode}
            </code>
            <button
              onClick={() => void navigator.clipboard?.writeText(company.joinCode)}
              className="text-[11px] text-brand-light hover:text-brand !min-h-0"
            >
              kopieren
            </button>
            <button
              onClick={async () => {
                if (confirm('Neuen Code erzeugen? Der alte gilt dann nicht mehr.')) {
                  await companiesApi.rotateCode(token, company.id);
                  await qc.invalidateQueries({ queryKey: ['companies'] });
                }
              }}
              className="text-[11px] text-slate-400 hover:text-slate-200 !min-h-0"
            >
              erneuern
            </button>
            <WageRescueButton companyId={company.id} companyName={company.name} />
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-ghost text-xs px-3 py-1.5 shrink-0">
          {open ? 'Zuklappen' : 'Mitglieder'}
        </button>
      </div>

      {open && (
        <div className="mt-4 border-t border-border/60 pt-3 space-y-3">
          {/* Mitglied hinzufügen */}
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex-1 min-w-[10rem]">
              <span className="block text-xs text-slate-400 mb-1">Mitarbeiter hinzufügen</span>
              <select
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
                className="input"
              >
                <option value="">— wählen —</option>
                {addable.map((u) => (
                  <option key={u.id} value={u.id} style={{ backgroundColor: '#0f172a', color: '#e6ebf3' }}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </label>
            <select
              value={addRole}
              onChange={(e) => setAddRole(e.target.value as CompanyRole)}
              className="input !w-auto"
            >
              <option value="employee">Mitarbeiter</option>
              <option value="owner">Chef</option>
            </select>
            <button
              onClick={async () => {
                if (!addUserId) return;
                await companiesApi.addMember(token, company.id, addUserId, addRole);
                setAddUserId('');
                await refresh();
              }}
              disabled={!addUserId}
              className="btn"
            >
              +
            </button>
          </div>

          {/* Mitglieder-Liste */}
          <ul className="space-y-1.5">
            {(members ?? []).map((m) => (
              <li key={m.userId} className="flex items-center gap-2 text-sm">
                <span className="flex-1 min-w-0 truncate">
                  {m.name} <span className="text-slate-500 text-xs">{m.email}</span>
                </span>
                <select
                  // Gesamtprüfung 11.09.2026: „Chef“ (owner) statt des Alt-Werts
                  // „Leitung“ (manager) — ein migrierter Chef wurde vorher als
                  // „Mitarbeiter“ angezeigt. Alt-'manager' wird als Chef dargestellt.
                  value={m.role === 'manager' ? 'owner' : m.role}
                  onChange={async (e) => {
                    await companiesApi.addMember(token, company.id, m.userId, e.target.value as CompanyRole);
                    await refresh();
                  }}
                  className="text-xs bg-slate-900 border border-border rounded px-2 py-1 !min-h-0"
                >
                  <option value="employee">Mitarbeiter</option>
                  <option value="owner">Chef</option>
                </select>
                <button
                  onClick={async () => {
                    if (confirm(`${m.name} aus „${company.name}" entfernen?`)) {
                      await companiesApi.removeMember(token, company.id, m.userId);
                      await refresh();
                    }
                  }}
                  className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2 py-1"
                >
                  ×
                </button>
              </li>
            ))}
            {members && members.length === 0 && (
              <li className="text-xs text-slate-500">Noch keine Mitglieder.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
